import { post } from 'jsx/utils/axiosWrapper';
import { trackEvent } from 'jsx/utils/metrics';
import { REQUEST_BRAND } from 'jsx/utils/brand';

declare global {
  interface Window {
    Kustomer?: Kustomer;
  }
}

interface Kustomer {
  start(options: KustomerStartOptions, callback?: () => void): Promise<void>;
  open(callback?: () => void): void;
  describeCustomer(
    options: { customAttributes: { chatPatientIdDevOnlyStr: string } },
    callback?: ({ customerId }: { customerId: string }) => void,
  ): Promise<void>;
  addListener(
    event: string,
    callback?: ({ conversationId }: { conversationId: string }) => void,
  ): void;
  describeConversation(options: KustomerConversationAttributes): Promise<void>;
}

interface KustomerStartOptions {
  brandId?: string;
  scheduleId?: string;
  chatIconSize: string;
  chatIconPosition: {
    alignment: string;
    verticalPadding: number;
    horizontalPadding: number;
  };
}

interface KustomerConversationAttributes {
  conversationId: string;
  customAttributes: {
    chatUrlStr: string;
  };
}

const scriptUrl = 'https://cdn.kustomerapp.com/chat-web/widget.js';

function findExistingScript(): HTMLScriptElement | null {
  return document.querySelector(`script[src="${scriptUrl}"]`);
}

function injectScript(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');

    script.onload = () => resolve();
    script.onerror = (error) => reject(error);

    script.async = true;
    script.setAttribute('data-kustomer-api-key', apiKey);
    script.src = scriptUrl;

    document.body.appendChild(script);
  });
}

function removeDropShadowFromKustomerIcon() {
  const iframe = document.getElementById(
    'kustomer-ui-sdk-iframe',
  ) as HTMLIFrameElement | null;

  if (iframe) {
    const style = document.createElement('style');
    style.append('* { filter: none !important; box-shadow: none; }');
    iframe.contentDocument?.head.appendChild(style);
  }
}

function createChatWidget(
  kustomer: Kustomer,
  brandId: string,
  scheduleId: string,
  patientId: string,
): Promise<void> {
  return new Promise((resolve) => {
    const chatWidgetViewedEventName = 'Chat Widget Viewed';
    const chatWidgetInitiatedEventName = 'Chat Initiated';

    const { isAgency } = REQUEST_BRAND;

    const chatIconSizeValue = isAgency ? '50' : '48';

    kustomer.start(
      {
        brandId,
        scheduleId,
        chatIconSize: chatIconSizeValue,
        chatIconPosition: {
          alignment: 'right',
          verticalPadding: 8,
          horizontalPadding: 8,
        },
      },
      () => {
        // Remove the drop shadow from the chat icon when it renders
        removeDropShadowFromKustomerIcon();

        // Send event to Amplitude for rendering the chat
        trackEvent(chatWidgetViewedEventName, {
          chatUrl: window.location.href,
        });

        kustomer.describeCustomer(
          {
            // Add patient ID to custom attribute chatPatientIdDevOnlyStr in Kustomer's Customer object
            // to verify that the patient's ID matches later when verifying patient in Kustomer
            customAttributes: {
              chatPatientIdDevOnlyStr: patientId,
            },
          },
          ({ customerId }: { customerId: string }) => {
            kustomer.addListener('onConversationCreate', (response) => {
              // Add the URL that chat was accessed from into the `chatUrlStr` Kustomer insight property
              kustomer.describeConversation({
                conversationId: response.conversationId,
                customAttributes: {
                  chatUrlStr: window.location.href,
                },
              });

              // Send event to Amplitude for initiating the chat
              trackEvent(chatWidgetInitiatedEventName, {
                chatUrl: window.location.href,
              });

              post('/api/patient/link-kustomer-chat-to-patient', {
                customerId,
              });
            });

            resolve();
          },
        );
      },
    );
  });
}

function openChat(kustomer: Kustomer): Promise<void> {
  return new Promise((resolve) => {
    kustomer.open(resolve);
  });
}

export interface KustomerOptions {
  apiKey: string;
  brandId: string;
  scheduleId: string;
}

export interface KustomerOptionsWithPatientId extends KustomerOptions {
  patientId: string;
}

export default async function openKustomerChat({
  apiKey,
  brandId,
  scheduleId,
  patientId,
}: KustomerOptionsWithPatientId): Promise<void> {
  if (!findExistingScript()) {
    await injectScript(apiKey);
    await createChatWidget(window.Kustomer!, brandId, scheduleId, patientId);
  }

  await openChat(window.Kustomer!);
}

export async function initiateKustomerChat({
  apiKey,
  brandId,
  scheduleId,
  patientId,
}: KustomerOptionsWithPatientId): Promise<void> {
  if (!findExistingScript()) {
    await injectScript(apiKey);
    await createChatWidget(window.Kustomer!, brandId, scheduleId, patientId);
  }
}
